import { Link } from "wouter";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

export const TV_PATH = "/tv";
export const tvImg = `${import.meta.env.BASE_URL}images/`;
export const CONTACT_HREF = "/contact";

export const trackTv = (page: string, placement: string, destination: string) =>
  trackEvent("relevant_tv_intent", { placement, destination, page_path: page });

export const tvDetailPages = [
  { href: "/tv/content-and-layouts", n: "01", title: "Content and layouts", body: "Media library, playlists, content zones, tickers, and built-in apps." },
  { href: "/tv/scheduling", n: "02", title: "Scheduling", body: "Day and time programming, date ranges, and content that stops on time." },
  { href: "/tv/screen-management", n: "03", title: "Screen management", body: "Players, remote controls, offline playback, and device diagnostics." },
];

export const layoutTypes = [
  { id: "full", cls: "l-full", name: "Full screen", desc: "One playlist, edge to edge.", cells: ["main"] },
  { id: "side", cls: "l-side", name: "Main + sidebar", desc: "Left or right sidebar zone.", cells: ["main", "side"] },
  { id: "tick", cls: "l-tick", name: "Main + ticker", desc: "Scrolling text, top or bottom.", cells: ["main", "tick"] },
  { id: "lbar", cls: "l-lbar", name: "L-bar", desc: "Main, sidebar, and ticker.", cells: ["main", "side", "tick"] },
];

export function LayoutDiagrams({ page }: { page: string }) {
  return (
    <ul className="rtv-screens" aria-label="Screen layout options">
      {layoutTypes.map((l) => (
        <li key={l.id} className="rtv-screen" data-testid={`layout-${l.id}-${page}`}>
          <figure className={l.cls} aria-hidden="true">
            {l.cells.map((c) => <i key={c} className={c} />)}
          </figure>
          <b>{l.name}</b>
          <span>{l.desc}</span>
        </li>
      ))}
    </ul>
  );
}

export function ExploreRows({ page, exclude }: { page: string; exclude?: string }) {
  return (
    <div className="rtv-rows">
      {tvDetailPages.filter((p) => p.href !== exclude).map((p) => (
        <Link key={p.href} href={p.href} className="rtv-row" data-testid={`link-tv-detail-${p.href.split("/").pop()}`} onClick={() => trackTv(page, "explore", p.href)}>
          <em>{p.n}</em><b>{p.title}</b><span>{p.body}</span><ArrowRight aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

export function TvCta({ page, title, body }: { page: string; title: string; body: string }) {
  return (
    <section className="rtv-cta" aria-labelledby="rtv-cta-title">
      <div className="rtv-wrap rtv-cta-inner">
        <div>
          <span className="rtv-kicker" style={{ color: "hsl(178 70% 62%)" }}>Relevant TV</span>
          <h2 id="rtv-cta-title">{title}</h2>
          <p>{body}</p>
        </div>
        <Link href={CONTACT_HREF} className="rtv-btn light" data-testid="link-tv-final-contact" onClick={() => trackTv(page, "final", CONTACT_HREF)}>
          Get started today <ArrowUpRight aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

export interface DetailSection { id: string; title: string; body: string; items?: string[]; note?: string; extra?: React.ReactNode }

export function TvDetailLayout({ path, crumb, kicker, title, lead, sections }: { path: string; crumb: string; kicker: string; title: string; lead: string; sections: DetailSection[] }) {
  return (
    <div className="rtv">
      <section className="rtv-dhero" aria-labelledby="rtv-dtitle">
        <div className="rtv-wrap">
          <nav className="rtv-crumbs" aria-label="Breadcrumb">
            <ol>
              <li><Link href="/">Home</Link></li>
              <li><Link href={TV_PATH} data-testid="link-tv-breadcrumb">Relevant TV</Link></li>
              <li aria-current="page">{crumb}</li>
            </ol>
          </nav>
          <span className="rtv-kicker">{kicker}</span>
          <h1 id="rtv-dtitle">{title}</h1>
          <p className="lead">{lead}</p>
          <div className="rtv-actions" style={{ marginTop: 32 }}>
            <Link href={CONTACT_HREF} className="rtv-btn light" data-testid="link-tv-detail-hero-contact" onClick={() => trackTv(path, "hero", CONTACT_HREF)}>Get started today <ArrowUpRight aria-hidden="true" /></Link>
            <Link href={TV_PATH} className="rtv-link" style={{ color: "hsl(60 20% 97%)" }} data-testid="link-tv-detail-back">Back to Relevant TV <ArrowRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <div className="rtv-wrap rtv-dbody">
        <nav className="rtv-toc" aria-label="On this page">
          <b>On this page</b>
          {sections.map((s) => <a key={s.id} href={`#${s.id}`} data-testid={`link-toc-${s.id}`}>{s.title}</a>)}
        </nav>
        <div>
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="rtv-dsec" aria-labelledby={`${s.id}-h`}>
              <h2 id={`${s.id}-h`}>{s.title}</h2>
              <p>{s.body}</p>
              {s.items && <ul className="rtv-ticks">{s.items.map((i) => <li key={i}>{i}</li>)}</ul>}
              {s.extra}
              {s.note && <p className="rtv-note">{s.note}</p>}
            </section>
          ))}
        </div>
      </div>

      <section className="rtv-next" aria-labelledby="rtv-next-title">
        <div className="rtv-wrap">
          <span className="rtv-kicker">Keep exploring</span>
          <h2 id="rtv-next-title">More of what Relevant TV does.</h2>
          <ExploreRows page={path} exclude={path} />
          <p style={{ marginTop: 28 }}><Link href={TV_PATH} className="rtv-link" data-testid="link-tv-overview">Relevant TV overview <ArrowRight aria-hidden="true" /></Link></p>
        </div>
      </section>

      <TvCta page={path} title="Put the right message on the right screen." body="Tell us about your showroom, service lounge, and other customer-facing screens. We will walk through setup, supported players, and how Relevant TV fits your store." />
    </div>
  );
}
